import { useQuery } from "@apollo/client";
import ContentCopy from "@mui/icons-material/ContentCopy";
import Search from "@mui/icons-material/Search";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Drawer,
  IconButton,
  InputAdornment,
  Link as MuiLink,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { useAlerts } from "components/Alerts/AlertProvider";
import { useCopyToClipboard } from "hooks/useCopyToClipboard";
import { useDebounce } from "hooks/useDebounce";
import { useState } from "react";
import { Link as RouterLink } from "react-router-dom";

import type { FlagRow } from "./FlagsTab";

import EncampLogo from "../../../assets/EncampLogo.svg";
import { gql } from "../../../generated-graphql";
import { formatPercent } from "./flagAdoption";
import { FlagAnnotationEditor } from "./FlagAnnotationEditor";

/**
 * Pulls the paginated list of tenants that have a TenantConfig override
 * for this flag with `value=true`. The `hasFlag:<key>` omnisearch token
 * is the existing server-side filter shared with the Staff Organizations
 * grid, so we re-use it here rather than introducing a flag-specific
 * resolver. Pagination is page-size based (rather than cursor-based) so
 * the "Load more" button can simply bump the size and re-fetch — Apollo
 * caches by `(search, pageSize)` so each step is one network round-trip.
 */
const FLAG_TENANTS_QUERY = gql(`
  query StaffFlagTenants($search: String!, $pageSize: Int!) {
    tenants(search: $search, pageSize: $pageSize, sort: [{ field: "name", sort: "asc" }]) {
      items {
        id
        name
        isDemo
        isDisabled
      }
      count
    }
  }
`);

const TENANTS_LIST_INITIAL_PAGE_SIZE = 100;
const TENANTS_LIST_LOAD_MORE_INCREMENT = 100;

function PropertyStrip({ flag }: { flag: FlagRow }) {
  const typeLabel = flag.type === "MODULE" ? "Module" : flag.type === "ADD_ON" ? "Add-on" : "Other";
  return (
    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
      <Chip label={typeLabel} size="small" />
      <Tooltip
        title={`This flag defaults to ${
          flag.defaultValue ? "ON" : "OFF"
        } for all tenants unless explicitly set`}
      >
        <Chip label={flag.defaultValue ? "Default: ON" : "Default: OFF"} size="small" />
      </Tooltip>
      {flag.staffOverride && (
        <Tooltip title="Staff users see this feature regardless of the flag value">
          <Chip
            size="small"
            // EncampLogo as an avatar to match the toggle panel's
            // icon-only affordance for staff-override flags. 12px keeps
            // it visually balanced with the chip's text height; the
            // earlier 16px overpowered the label.
            avatar={
              <Box
                component="img"
                src={EncampLogo}
                alt=""
                sx={{ width: 12, height: 12, ml: 0.5 }}
              />
            }
            label="Staff-only"
          />
        </Tooltip>
      )}
      {flag.inDevelopment && <Chip label="In development" size="small" />}
    </Stack>
  );
}

function DependencySection({
  title,
  keys,
  flagsByKey,
  onSelect,
}: {
  title: string;
  keys: string[];
  flagsByKey: Map<string, FlagRow>;
  onSelect: (flag: FlagRow) => void;
}) {
  if (keys.length === 0) return null;
  return (
    <Stack spacing={0.5}>
      <Typography variant="overline" color="text.secondary">
        {title}
      </Typography>
      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
        {keys.map((key) => {
          const dep = flagsByKey.get(key);
          if (!dep) {
            // Unknown key — render as a plain monospace chip so staff
            // see something is referenced even if metadata is stale.
            return <Chip key={key} label={key} size="small" />;
          }
          return (
            <Chip
              key={key}
              label={dep.label}
              size="small"
              clickable
              onClick={() => onSelect(dep)}
            />
          );
        })}
      </Stack>
    </Stack>
  );
}

/**
 * One adoption tile (Enabled or Disabled). Shows the effective total
 * (`count`) prominently and an optional `breakdown` caption beneath it
 * that splits explicit overrides from default-inherited tenants. Only
 * the side that matches the flag's `defaultValue` carries a breakdown —
 * the opposite side is always all-explicit by construction.
 */
function StatTile({
  label,
  count,
  denominator,
  breakdown,
  helpText,
}: {
  label: string;
  count: number;
  denominator: number;
  breakdown?: { explicit: number; fromDefault: number };
  helpText: string;
}) {
  return (
    <Tooltip title={helpText} placement="top" arrow>
      <Stack
        spacing={0.25}
        sx={{
          flex: 1,
          minWidth: 0,
          p: 1.5,
          borderRadius: 1,
          bgcolor: "background.default",
          border: 1,
          borderColor: "divider",
          cursor: "help",
        }}
      >
        <Typography variant="caption" color="text.secondary" noWrap>
          {label}
        </Typography>
        <Stack direction="row" alignItems="baseline" spacing={1} sx={{ minWidth: 0 }}>
          <Typography variant="h5" sx={{ fontVariantNumeric: "tabular-nums" }}>
            {count}
          </Typography>
          {denominator > 0 && (
            <Typography variant="caption" color="text.secondary">
              ({formatPercent(count, denominator)})
            </Typography>
          )}
        </Stack>
        {breakdown ? (
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ fontVariantNumeric: "tabular-nums" }}
          >
            {breakdown.explicit} explicit · {breakdown.fromDefault} from default
          </Typography>
        ) : (
          // Empty caption preserves the tile height so the two boxes
          // stay vertically aligned.
          <Typography variant="caption" color="text.secondary">
            &nbsp;
          </Typography>
        )}
      </Stack>
    </Tooltip>
  );
}

function AdoptionStats({
  flag,
  customerTenantCount,
}: {
  flag: FlagRow;
  customerTenantCount: number;
}) {
  // Tenants without a TenantConfig row inherit `defaultValue`. We only
  // include the customer-tenant denominator (active, non-demo) — see
  // useCustomerTenantCount.
  const noRowCount = Math.max(0, customerTenantCount - flag.tenantOnCount - flag.tenantOffCount);

  // Effective totals fold the no-row group into whichever side matches
  // the flag's default. By construction the *other* side is therefore
  // always all-explicit.
  const enabledTotal = flag.tenantOnCount + (flag.defaultValue ? noRowCount : 0);
  const disabledTotal = flag.tenantOffCount + (flag.defaultValue ? 0 : noRowCount);

  const enabledBreakdown = flag.defaultValue
    ? { explicit: flag.tenantOnCount, fromDefault: noRowCount }
    : undefined;
  const disabledBreakdown = flag.defaultValue
    ? undefined
    : { explicit: flag.tenantOffCount, fromDefault: noRowCount };

  return (
    <Stack spacing={1}>
      <Typography variant="overline" color="text.secondary">
        Tenant Adoption
      </Typography>
      <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
        <StatTile
          label="Enabled"
          count={enabledTotal}
          denominator={customerTenantCount}
          breakdown={enabledBreakdown}
          helpText={
            flag.defaultValue
              ? "Count of active customer tenants where this flag is either explicitly enabled or not set (this flag defaults to ON)"
              : "Count of active customer tenants that have this flag explicitly enabled"
          }
        />
        <StatTile
          label="Disabled"
          count={disabledTotal}
          denominator={customerTenantCount}
          breakdown={disabledBreakdown}
          helpText={
            flag.defaultValue
              ? "Count of active customer tenants that have this flag explicitly disabled"
              : "Count of active customer tenants where this flag is either explicitly disabled or not set (this flag defaults to OFF)"
          }
        />
      </Stack>
    </Stack>
  );
}

function TenantsOnList({ flag }: { flag: FlagRow }) {
  const [searchInput, setSearchInput] = useState("");
  // Debounce so the search box doesn't fire a network request on every
  // keystroke. 300ms feels responsive without being chatty for the
  // longest-tail flags (TierIIModule etc.) where the result set is
  // hundreds of tenants.
  const [debouncedSearch] = useDebounce(searchInput.trim(), 300);
  const [pageSize, setPageSize] = useState(TENANTS_LIST_INITIAL_PAGE_SIZE);

  // Compose the omnisearch query. Free-text terms after `hasFlag:KEY`
  // fall through to the resolver's default columns (`name`, `id`), so a
  // user typing `acme` sees only tenants with this flag whose name or id
  // matches "acme".
  const search = debouncedSearch ? `hasFlag:${flag.key} ${debouncedSearch}` : `hasFlag:${flag.key}`;

  const { data, loading, error } = useQuery(FLAG_TENANTS_QUERY, {
    variables: { search, pageSize },
    fetchPolicy: "cache-and-network",
  });

  const items = data?.tenants.items ?? [];
  const totalCount = data?.tenants.count;
  const hasMore = totalCount !== undefined && items.length < totalCount;
  const isFiltered = debouncedSearch.length > 0;

  return (
    <Stack spacing={1}>
      <Stack direction="row" alignItems="baseline" spacing={1}>
        <Typography variant="overline" color="text.secondary">
          Tenants with this flag enabled
        </Typography>
        {totalCount !== undefined && (
          <Typography variant="caption" color="text.secondary">
            (
            {isFiltered
              ? `${items.length} of ${totalCount} matching`
              : hasMore
                ? `${items.length} of ${totalCount}`
                : totalCount}
            )
          </Typography>
        )}
      </Stack>
      {flag.defaultValue && (
        <Typography variant="caption" color="text.secondary">
          Default-on flag: only tenants with explicit overrides are listed.
        </Typography>
      )}
      <TextField
        size="small"
        placeholder="Search tenants by name…"
        value={searchInput}
        onChange={(e) => {
          // Reset pagination when the search term changes — otherwise a
          // user who clicked "Load more" then narrowed their search
          // would silently keep fetching a 200/300 page when 100 will do.
          setSearchInput(e.target.value);
          setPageSize(TENANTS_LIST_INITIAL_PAGE_SIZE);
        }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <Search fontSize="small" />
            </InputAdornment>
          ),
        }}
      />
      {loading && !data && <CircularProgress size={16} />}
      {error && (
        <Alert severity="error" sx={{ mt: 0.5 }}>
          Failed to load tenants: {error.message}
        </Alert>
      )}
      {data && items.length === 0 && (
        <Typography variant="body2" color="text.secondary">
          {isFiltered
            ? "No tenants match that search."
            : "No tenants currently have this flag enabled."}
        </Typography>
      )}
      {data && items.length > 0 && (
        <Stack
          spacing={0}
          sx={{
            border: 1,
            borderColor: "divider",
            borderRadius: 1,
            maxHeight: 280,
            overflowY: "auto",
          }}
        >
          {items.map((tenant, idx) => (
            <Stack
              key={tenant.id}
              direction="row"
              alignItems="center"
              spacing={1}
              sx={{
                px: 1.5,
                py: 1,
                borderTop: idx === 0 ? 0 : 1,
                borderColor: "divider",
              }}
            >
              <MuiLink
                component={RouterLink}
                to={`/staff/implementation/${tenant.id}?tab=feature-flags`}
                underline="hover"
                sx={{ flex: 1, minWidth: 0 }}
              >
                <Typography variant="body2" noWrap>
                  {tenant.name}
                </Typography>
              </MuiLink>
              {tenant.isDemo && <Chip size="small" label="Demo" />}
              {tenant.isDisabled && <Chip size="small" label="Disabled" color="error" />}
            </Stack>
          ))}
        </Stack>
      )}
      {hasMore && (
        <Button
          size="small"
          variant="text"
          disabled={loading}
          onClick={() => setPageSize((s) => s + TENANTS_LIST_LOAD_MORE_INCREMENT)}
          sx={{ alignSelf: "flex-start" }}
        >
          {loading ? "Loading…" : `Load ${TENANTS_LIST_LOAD_MORE_INCREMENT} more`}
        </Button>
      )}
    </Stack>
  );
}

/**
 * Right-side drawer scoped to a single feature flag. Read-only
 * inspector + inline annotation editor.
 *
 * The `flagsByKey` map is supplied so the dependencies section can
 * render rich chips that drill from one flag's drawer to another
 * without re-querying.
 */
export function FlagDetailDrawer({
  flag,
  flagsByKey,
  customerTenantCount,
  onSelectFlag,
  onClose,
}: {
  flag: FlagRow | null;
  flagsByKey: Map<string, FlagRow>;
  customerTenantCount: number;
  onSelectFlag: (flag: FlagRow) => void;
  onClose: () => void;
}) {
  const alerts = useAlerts();
  const copyToClipboard = useCopyToClipboard();
  const [editing, setEditing] = useState(false);

  const onCopy = async () => {
    if (!flag) return;
    await copyToClipboard(flag.key);
    alerts.success(`Copied "${flag.key}"`);
  };

  // Reset the editor when switching between flags so a half-open editor
  // for flag A doesn't leak into flag B's drawer state.
  const headerKey = flag?.key ?? null;
  const childFlagKeys = flag
    ? Array.from(flagsByKey.values())
        .filter((f) => f.parentKey === flag.key)
        .map((f) => f.key)
    : [];

  return (
    <Drawer
      anchor="right"
      open={flag !== null}
      onClose={() => {
        setEditing(false);
        onClose();
      }}
      slotProps={{ paper: { sx: { scrollbarGutter: "stable" } } }}
    >
      <Box sx={{ width: { xs: "100vw", md: 560 }, p: 3, bgcolor: "background.paper" }}>
        {flag && (
          // `spacing={4}` provides the visual section break that the
          // previous <Divider />s gave us, but without the heavy chrome.
          <Stack spacing={4} key={headerKey}>
            <Stack spacing={2}>
              <Stack direction="row" alignItems="flex-start" spacing={1}>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="h5">{flag.label}</Typography>
                  <Stack direction="row" spacing={0.5} alignItems="center">
                    <Typography variant="caption" color="text.secondary">
                      Key:
                    </Typography>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ fontFamily: "monospace" }}
                    >
                      {flag.key}
                    </Typography>
                    {/* Copy is intentionally key-only — the label "Key:"
                        is decorative; staff almost always want to paste
                        the bare key into code or queries. */}
                    <Tooltip title="Copy key">
                      <IconButton size="small" onClick={onCopy} aria-label="Copy key">
                        <ContentCopy fontSize="inherit" />
                      </IconButton>
                    </Tooltip>
                  </Stack>
                </Box>
              </Stack>

              <PropertyStrip flag={flag} />

              <Typography variant="body2">{flag.description}</Typography>
            </Stack>

            {/* Suppress the entire dependency section (and its
                surrounding spacing) when there are no parent / requires
                / add-on relationships to show — otherwise the empty
                wrapper produces double spacing between the description
                and "Tenant Adoption". */}
            {(flag.parentKey || flag.requires.length > 0 || childFlagKeys.length > 0) && (
              <Stack spacing={2}>
                <DependencySection
                  title="Parent module"
                  keys={flag.parentKey ? [flag.parentKey] : []}
                  flagsByKey={flagsByKey}
                  onSelect={onSelectFlag}
                />
                <DependencySection
                  title="Requires"
                  keys={[...flag.requires]}
                  flagsByKey={flagsByKey}
                  onSelect={onSelectFlag}
                />
                <DependencySection
                  title="Add-ons"
                  keys={childFlagKeys}
                  flagsByKey={flagsByKey}
                  onSelect={onSelectFlag}
                />
              </Stack>
            )}

            <AdoptionStats flag={flag} customerTenantCount={customerTenantCount} />

            {/* Annotation deliberately renders above the tenants list:
                the list scrolls inside its own pane and can grow long
                (100+ tenants), so keeping notes/tags higher up means
                staff don't have to scroll past it to add a comment. */}
            <Stack spacing={1}>
              <Stack direction="row" alignItems="baseline" justifyContent="space-between">
                <Typography variant="overline" color="text.secondary">
                  Annotation
                </Typography>
                {!editing && (
                  <Button size="small" onClick={() => setEditing(true)}>
                    Edit notes &amp; tags
                  </Button>
                )}
              </Stack>
              {editing ? (
                <FlagAnnotationEditor
                  flag={flag}
                  onCancel={() => setEditing(false)}
                  onSaved={() => setEditing(false)}
                />
              ) : (
                // `spacing={1.5}` separates the three rows (notes,
                // tags, last-edited) enough that the tag chips feel
                // distinct from the notes paragraph above them; tighter
                // values made the chips read like a tail of the
                // sentence.
                <Stack spacing={1.5}>
                  {flag.notes ? (
                    <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
                      {flag.notes}
                    </Typography>
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      No notes yet.
                    </Typography>
                  )}
                  {flag.tags.length > 0 && (
                    <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                      {flag.tags.map((tag) => (
                        <Chip key={tag} label={tag} size="small" />
                      ))}
                    </Stack>
                  )}
                  {flag.annotationUpdatedAt && (
                    <Typography variant="caption" color="text.secondary">
                      Last edited {new Date(flag.annotationUpdatedAt).toLocaleString()}
                      {flag.annotationUpdatedBy ? ` by ${flag.annotationUpdatedBy}` : ""}
                    </Typography>
                  )}
                </Stack>
              )}
            </Stack>

            <TenantsOnList flag={flag} />
          </Stack>
        )}
      </Box>
    </Drawer>
  );
}
